<template>
  <main-layout>
    <div class="shutdown-page text-left">
      <h1 class="title content-header">Shutdown</h1>
      <v-alert v-if="error" type="error">{{ error }}</v-alert>
      <v-alert v-if="message" type="success">{{ message }}</v-alert>
      <v-btn v-if="submitted" class="mb-4" outlined @click="newCampaign">Préparer une nouvelle campagne</v-btn>
      <v-card outlined class="pa-5 mb-6">
        <h2 class="subtitle-1 font-weight-bold mb-3">Mode de la plateforme</h2>
        <p v-if="$isReadOnly">Indeego est en lecture seule depuis {{ date(platform.readOnlySince) }}.</p>
        <p v-else-if="$canWrite">Les écritures sont autorisées.</p>
        <p v-else>État de la plateforme indisponible.</p>
        <v-btn outlined :loading="changingPlatform" :disabled="changingPlatform || !platform.mode" @click="platformConfirmation = true">
          {{ $isReadOnly ? 'Réactiver les écritures' : 'Passer Indeego en lecture seule' }}
        </v-btn>
        <v-switch :input-value="platform.exportsVisible" label="Afficher l’accès aux exports"
          :loading="changingVisibility" :disabled="changingVisibility || !platform.mode" @change="setExportsVisible" />
        <p class="text-body-2">Affiche les liens dans le menu et le bandeau. La page reste accessible directement aux administrateurs autorisés.</p>
      </v-card>
      <v-card outlined class="pa-5 mb-6">
        <h2 class="subtitle-1 font-weight-bold mb-3">Annonce et relances</h2>
        <v-alert v-if="policy && !policy.ready" type="warning">
          Configuration du runner indisponible ou trop ancienne. Vérifiez que le runner est démarré.
          La création de campagnes est indisponible.
        </v-alert>
        <v-alert v-else-if="policy && policy.disabled" type="warning">Emails désactivés : les envois seront ignorés sans reprise ultérieure.</v-alert>
        <v-alert v-else-if="policy && policy.catchall" type="warning">Catchall actif : tous les emails seront redirigés vers {{ policy.catchall }}.</v-alert>
        <v-alert v-else-if="policy" type="info">Envoi réel aux administrateurs des clients sélectionnés.</v-alert>
        <p>Un email individuel est prévu par adresse et par client. Les dates du mail ne déclenchent pas automatiquement la lecture seule.</p>
        <v-select v-model="kind" :items="kinds" label="Type de message" outlined dense :disabled="Boolean(sending)" />
        <v-row>
          <v-col cols="12" md="6"><v-text-field v-model="readOnlyDate" label="Date de passage en lecture seule" type="date" outlined dense :disabled="Boolean(sending)" /></v-col>
          <v-col cols="12" md="6"><v-text-field v-model="closureDate" label="Date de fermeture définitive" type="date" outlined dense :disabled="Boolean(sending)" /></v-col>
        </v-row>
        <v-alert v-if="dateError" type="warning" dense>{{ dateError }}</v-alert>
        <v-text-field v-model="search" label="Rechercher un client ou un espace" prepend-inner-icon="search" clearable outlined dense />
        <v-data-table v-model="selected" :headers="clientHeaders" :items="clients" :search="search" item-key="id" :loading="loading"
          :items-per-page="10" :show-select="!sending" no-data-text="Aucun client avec un espace Indeego" no-results-text="Aucun client trouvé">
          <template v-slot:item.recipientCount="{ item }">
            <span :class="{ 'warning--text': !item.recipientCount }">{{ item.recipientCount }}{{ item.recipientCount ? '' : ' (aucun destinataire)' }}</span>
          </template>
        </v-data-table>
        <p class="mt-4">{{ selected.length }} client(s) sélectionné(s), {{ estimatedRecipients }} destinataire(s) estimé(s).</p>
        <v-btn color="primary" :loading="previewing" :disabled="!valid || Boolean(sending)" @click="loadPreview">Prévisualiser</v-btn>
        <template v-if="preview">
          <v-divider class="my-5" />
          <p><strong>{{ preview.recipientCount }} destinataire(s)</strong> sur {{ preview.clientCount }} client(s). Exemple personnalisé pour un destinataire.</p>
          <v-alert v-for="(warning, index) in preview.warnings" :key="index" type="warning" dense>{{ warning }}</v-alert>
          <template v-if="preview.sample">
          <p><strong>Objet :</strong> {{ preview.sample.subject }}</p>
          <iframe title="Aperçu du mail de shutdown" sandbox="" referrerpolicy="no-referrer" :srcdoc="preview.sample.html" class="mail-preview" />
          <div class="mt-4">
            <v-btn outlined :loading="sending === 'test'" :disabled="Boolean(sending) || !valid" @click="submit('test')">M’envoyer un test</v-btn>
            <v-btn class="ml-3" color="primary" :disabled="Boolean(sending) || !valid || !preview.recipientCount" @click="confirmation = true">Préparer l’envoi</v-btn>
          </div>
          </template>
          <p v-else>Aucun destinataire disponible pour cet aperçu. Sélectionnez un client avec un administrateur actif disposant d’une adresse email.</p>
        </template>
      </v-card>
      <v-card outlined class="pa-5 mb-6">
        <h2 class="subtitle-1 font-weight-bold mb-3">Historique des campagnes</h2>
        <p>Les emails sont traités par le runner, avec 5 secondes entre deux envois. La pause concerne les emails restant à envoyer.</p>
        <v-btn text :loading="refreshing" @click="refreshHistory">Actualiser</v-btn>
        <v-data-table :headers="historyHeaders" :items="campaigns" :items-per-page="10" no-data-text="Aucune campagne">
          <template v-slot:item.createdAt="{ item }">{{ date(item.createdAt) }}</template>
          <template v-slot:item.kind="{ item }">{{ kindLabel(item.kind) }}{{ item.isTest ? ' (test)' : '' }}</template>
          <template v-slot:item.status="{ item }">{{ statusLabel(item.status) }}</template>
          <template v-slot:item.counts="{ item }">
            <span v-if="item.counts">{{ item.counts.sent }} envoyé(s), {{ item.counts.skipped }} ignoré(s), {{ item.counts.failed }} échec(s),
              {{ item.counts.queued + item.counts.sending }} en attente</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn v-if="item.status === 'paused'" small text :loading="updating === item.id" :disabled="Boolean(updating)" @click="setPaused(item, false)">Reprendre</v-btn>
            <v-btn v-else-if="['queued', 'running', 'sending'].includes(item.status)" small text :loading="updating === item.id"
              :disabled="Boolean(updating)" @click="setPaused(item, true)">Mettre en pause</v-btn>
          </template>
        </v-data-table>
      </v-card>
      <v-dialog v-model="confirmation" max-width="650" :persistent="Boolean(sending)">
        <v-card v-if="preview"><v-card-title>Confirmer la campagne</v-card-title><v-card-text>
          <p>{{ kindLabel(kind) }} : {{ preview.recipientCount }} destinataire(s), {{ preview.clientCount }} client(s).</p>
          <p>Clients : {{ selected.map(client => client.name).join(', ') }}</p>
          <p>Lecture seule : {{ day(readOnlyDate) }}. Fermeture : {{ day(closureDate) }}.</p>
          <p v-if="policy.disabled">Emails désactivés : cette campagne sera ignorée sans reprise.</p>
          <p v-else-if="policy.catchall">Tous les emails iront vers {{ policy.catchall }}.</p>
          <p v-else>Les administrateurs recevront réellement ces emails.</p>
        </v-card-text><v-card-actions><v-spacer /><v-btn text :disabled="Boolean(sending)" @click="confirmation = false">Annuler</v-btn>
          <v-btn color="primary" :loading="sending === 'campaigns'" :disabled="Boolean(sending) || !valid" @click="submit('campaigns')">Confirmer l’envoi</v-btn>
        </v-card-actions></v-card>
      </v-dialog>
      <v-dialog v-model="platformConfirmation" max-width="550" :persistent="changingPlatform">
        <v-card><v-card-title>Modifier le mode d’Indeego</v-card-title><v-card-text>
          {{ $isReadOnly ? 'Réactiver les écritures pour tous les clients ?' : 'Bloquer les écritures pour tous les clients dès maintenant ? La consultation et les exports resteront disponibles.' }}
        </v-card-text><v-card-actions><v-spacer /><v-btn text :disabled="changingPlatform" @click="platformConfirmation = false">Annuler</v-btn>
          <v-btn color="primary" :loading="changingPlatform" @click="toggleReadOnly">Confirmer</v-btn>
        </v-card-actions></v-card>
      </v-dialog>
    </div>
  </main-layout>
</template>

<script>
import { uuid } from 'vue-uuid';
// eslint-disable-next-line import/no-cycle
import api from '@/api';
import MainLayout from '@/views/indeegoSettings/MainLayout.vue';

export default {
  components: { MainLayout },
  data: () => ({
    clients: [],
    selected: [],
    campaigns: [],
    policy: null,
    preview: null,
    search: ``,
    kind: `announcement`,
    readOnlyDate: ``,
    closureDate: ``,
    error: ``,
    message: ``,
    loading: false,
    refreshing: false,
    previewing: false,
    sending: null,
    updating: null,
    changingPlatform: false,
    changingVisibility: false,
    platformConfirmation: false,
    confirmation: false,
    timer: null,
    keys: {},
    previewRevision: 0,
    submitted: false,
    kinds: [
      { value: `announcement`, text: `Annonce de la fermeture` },
      { value: `reminderBefore`, text: `Relance avant la lecture seule` },
      { value: `reminderAfter`, text: `Relance après la lecture seule` },
    ],
    clientHeaders: [{ text: `Client`, value: `name` }, { text: `Espace`, value: `slug` },
      { text: `Administrateurs actifs`, value: `administratorCount` }, { text: `Destinataires`, value: `recipientCount` }],
    historyHeaders: [{ text: `Créée le`, value: `createdAt` }, { text: `Message`, value: `kind` },
      { text: `Statut`, value: `status` }, { text: `Envois`, value: `counts`, sortable: false }, { text: `Actions`, value: `actions`, sortable: false }],
  }),
  computed: {
    platform() { return this.$store.state.platform; },
    payload() {
      return {
        clientIds: this.selected.map(client => client.id).sort((a, b) => a - b), kind: this.kind, readOnlyDate: this.readOnlyDate, closureDate: this.closureDate,
      };
    },
    fingerprint() { return JSON.stringify(this.payload); },
    estimatedRecipients() { return this.selected.reduce((count, client) => count + client.recipientCount, 0); },
    dateError() {
      if (!this.readOnlyDate || !this.closureDate) return ``;
      if (this.closureDate <= this.readOnlyDate) return `La fermeture doit être postérieure au passage en lecture seule.`;
      const today = new Date().toLocaleDateString(`sv-SE`, { timeZone: `Europe/Paris` });
      if (this.closureDate < today) return `La date de fermeture ne peut pas être passée.`;
      if (this.kind === `announcement` && this.readOnlyDate < today) return `Pour une annonce, la date de lecture seule ne peut pas être passée.`;
      if (this.kind === `reminderBefore` && this.readOnlyDate <= today) return `Pour cette relance, la date de lecture seule doit être dans le futur.`;
      if (this.kind === `reminderAfter` && this.readOnlyDate > today) return `Pour cette relance, la date de lecture seule doit être passée.`;
      return ``;
    },
    valid() { return Boolean(this.policy && this.policy.ready && this.selected.length && this.readOnlyDate && this.closureDate && !this.dateError); },
  },
  watch: {
    fingerprint() { this.preview = null; this.confirmation = false; this.previewRevision += 1; },
  },
  mounted() { this.load(); this.timer = setInterval(() => { if (!document.hidden) { this.refreshHistory(); this.refreshPolicy(); } }, 15000); },
  beforeDestroy() { clearInterval(this.timer); this.previewRevision += 1; },
  methods: {
    date(value) { return value ? new Date(value).toLocaleString(`fr-FR`) : `-`; },
    day(value) { return value ? value.split(`-`).reverse().join(`/`) : `-`; },
    kindLabel(kind) { const item = this.kinds.find(item => item.value === kind); return item ? item.text : kind; },
    statusLabel(status) {
      return ({
        queued: `En attente`, running: `En cours`, sending: `En cours`, paused: `En pause`, completed: `Terminée`, done: `Terminée`, failed: `Échec`,
      })[status] || status;
    },
    fail(error, fallback) { this.error = (error.response && error.response.data && error.response.data.message) || fallback; },
    async load() {
      this.loading = true;
      try {
        const [clients, config] = await Promise.all([api.get(`/shutdown/clients`), api.get(`/shutdown/config`)]);
        this.clients = clients.data.items; this.policy = config.data;
      } catch (error) { this.fail(error, `Impossible de charger les clients ou la configuration des envois.`); } finally { this.loading = false; }
      this.refreshHistory();
    },
    async refreshPolicy() {
      try { const { data } = await api.get(`/shutdown/config`); this.policy = data; } catch (error) { this.policy = { ready: false }; }
    },
    async refreshHistory() {
      if (this.refreshing) return;
      this.refreshing = true;
      try {
        const { data } = await api.get(`/shutdown/campaigns`); this.campaigns = data.items;
      } catch (error) { this.fail(error, `Impossible de charger l’historique.`); } finally { this.refreshing = false; }
    },
    async loadPreview() {
      if (!this.valid || this.previewing) return;
      this.previewing = true; this.error = ``; this.message = ``;
      const revision = this.previewRevision;
      try {
        const { data } = await api.post(`/shutdown/preview`, this.payload);
        if (revision === this.previewRevision) { this.preview = data; this.policy = data.policy; }
      } catch (error) { this.fail(error, `Impossible de préparer l’aperçu.`); } finally { this.previewing = false; }
    },
    newCampaign() { this.keys = {}; this.preview = null; this.message = ``; this.submitted = false; this.previewRevision += 1; },
    async submit(type) {
      if (this.sending || !this.preview || !this.preview.sample || !this.preview.recipientCount || !this.valid) return;
      this.sending = type; this.error = ``; this.message = ``;
      const key = `${type}:${this.fingerprint}`;
      if (!this.keys[key]) this.$set(this.keys, key, uuid.v4());
      try {
        await api.post(`/shutdown/${type}`, { ...this.payload, audienceToken: this.preview.audienceToken, requestKey: this.keys[key] });
        this.confirmation = false; this.submitted = true;
        this.message = type === `test`
          ? `Le test a été mis en attente pour votre adresse. Le runner appliquera ses règles d’envoi.`
          : `La campagne a été enregistrée. Le runner traitera les destinataires progressivement.`;
        await this.refreshHistory();
      } catch (error) {
        if (error.response && error.response.status === 409) { this.preview = null; this.confirmation = false; }
        this.fail(error, `Impossible de confirmer l’envoi. Vous pouvez réessayer sans créer de doublon.`);
      } finally { this.sending = null; }
    },
    async setPaused(item, paused) {
      if (this.updating) return;
      this.updating = item.id; this.error = ``;
      try {
        await api.post(`/shutdown/campaigns/${item.id}/${paused ? `pause` : `resume`}`); await this.refreshHistory();
      } catch (error) { this.fail(error, `Impossible de modifier cette campagne.`); } finally { this.updating = null; }
    },
    async setExportsVisible(value) {
      if (this.changingVisibility) return;
      this.changingVisibility = true; this.error = ``;
      try {
        await this.$store.dispatch(`platform/setExportsVisible`, Boolean(value));
      } catch (error) { this.fail(error, `Impossible de modifier la visibilité des exports.`); } finally { this.changingVisibility = false; }
    },
    async toggleReadOnly() {
      if (this.changingPlatform) return;
      this.changingPlatform = true; this.error = ``;
      try {
        await this.$store.dispatch(`platform/setReadOnly`, !this.$isReadOnly); this.platformConfirmation = false;
      } catch (error) { this.fail(error, `Impossible de modifier le mode de la plateforme.`); } finally { this.changingPlatform = false; }
    },
  },
};
</script>

<style scoped>
.shutdown-page { max-width: 1200px; }
.mail-preview { width: 100%; height: 650px; border: 1px solid #ddd; background: white; }
</style>
